import React, { useState } from 'react';
import DeviceModal from './DeviceModal';
import EnterpriseDeviceCluster from './EnterpriseDeviceCluster';
import Device from './Device';
import PurchaseModal from './PurchaseModal';
import { useWallet } from '../context/WalletContext';
import '../styles/products.css'; // Keep the original CSS import

const ProductsGrid = () => {
  // State to manage modal visibility
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [currentDevice, setCurrentDevice] = useState(null);
  
  // State for purchase modal
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // State for staking notification
  const [stakingNotification, setStakingNotification] = useState(false);
  
  // Get wallet context
  const { wallet, openWalletModal } = useWallet();

  const openInfoModal = (device) => {
    setCurrentDevice(device);
    setInfoModalOpen(true);
  };

  const closeInfoModal = () => {
    setInfoModalOpen(false);
  };
  
  const openPurchaseModal = (product) => {
    const productDetails = {
      id: product,
      name: getProductName(product),
      price: getProductPrice(product),
      miningFactor: getProductMiningFactor(product),
      baseAPY: getProductBaseAPY(product), // The No-Stake APY rate
      maxAPY: getProductMaxAPY(product),
      stakingCapacity: getProductStakingCapacity(product)
    };
    
    setSelectedProduct(productDetails);
    
    // First check if wallet is connected
    if (!wallet.connected) {
      openWalletModal();
      return;
    }
    
    // If wallet is connected, open purchase modal
    setPurchaseModalOpen(true);
  };
  
  const closePurchaseModal = () => {
    setPurchaseModalOpen(false);
  };
  
  const handlePurchaseComplete = (transaction) => {
    console.log('Purchase completed:', transaction);
    // Here you would typically add code to update the user's portfolio
    setTimeout(() => {
      setPurchaseModalOpen(false);
    }, 1000);
  };

  // Show staking notice when user attempts to select a staking tier
  const showStakingNotice = () => {
    setStakingNotification(true);
    setTimeout(() => {
      setStakingNotification(false);
    }, 3000);
  };
  
  // Helper functions to get product details
  const getProductName = (id) => {
    const names = {
      basic: 'AeroNode Basic',
      pro: 'AeroNode Pro',
      elite: 'AeroNode Elite',
      enterprise: 'AeroNode Enterprise'
    };
    return names[id] || '';
  };
  
  const getProductPrice = (id) => {
    const prices = {
      basic: '499',
      pro: '999',
      elite: '1,999',
      enterprise: '4,999'
    };
    return prices[id] || '';
  };
  
  const getProductMiningFactor = (id) => {
    const factors = {
      basic: '1.0',
      pro: '2.0',
      elite: '3.0',
      enterprise: '6.0'
    };
    return factors[id] || '';
  };
  
  const getProductBaseAPY = (id) => {
    const apys = {
      basic: '30%',
      pro: '40%',
      elite: '50%',
      enterprise: '60%'
    };
    return apys[id] || '';
  };
  
  const getProductMaxAPY = (id) => {
    const apys = {
      basic: '69%',
      pro: '180%',
      elite: '350%',
      enterprise: '600%'
    };
    return apys[id] || '';
  };
  
  const getProductStakingCapacity = (id) => {
    const capacities = {
      basic: '10,000 AFI',
      pro: '25,000 AFI',
      elite: '60,000 AFI',
      enterprise: '160,000 AFI'
    };
    return capacities[id] || '';
  };
  
  // Helper function for maximum APY (for the third tier)
  const getProductMaximumAPY = (id) => {
    const apys = {
      basic: '100%',
      pro: '250%',
      elite: '450%',
      enterprise: '800%'
    };
    return apys[id] || '';
  };

  // Helper function to get the correct staking info text based on product type
  const getStakingInfoText = (type) => {
    if (type === 'basic' || type === 'pro') {
      return "Yield is calculated in AFI tokens. USD returns will vary with token price.";
    } else if (type === 'elite') {
      return "Yield represents token returns; actual USD value depends on protocol revenue and market conditions.";
    } else if (type === 'enterprise') {
      return "Expected yields may be reduced if network emission capacity or protocol revenue is insufficient";
    }
    return "";
  };

  // Helper to render a product card
  const renderProductCard = (type, label) => {
    return (
      <div className="product-card">
        <div className="product-header">
          <div className="product-label">{label}</div>
          <div className="early-adopter-label">+50% BONUS</div>
          {type === 'enterprise' ? <EnterpriseDeviceCluster /> : <Device type={type} />}
          <h3 className="product-title">{getProductName(type)}</h3>
        </div>
        <div className="product-body">
          <div className="product-price">
            <span className="price-currency">USDT</span>{getProductPrice(type)}
          </div>
          <div className="product-specs">
            <div className="spec-item">
              <svg className="spec-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2"/>
              </svg>
              <span>Mining Factor: {getProductMiningFactor(type)}x</span>
            </div>
            {renderProductSpecs(type)}
          </div>
          
          <div className="staking-section">
            <h4 className="staking-title">Return Rate</h4>
            <div className="current-apy">
              <div className="apy-value">{getProductBaseAPY(type)}</div>
              <div className="apy-label">Expected Annual Yield</div>
            </div>
            <div className="staking-options">
              <div className="staking-option active">
                <div className="option-name">No Stake</div>
                <div className="option-value">{getProductBaseAPY(type)}</div>
              </div>
              <div className="staking-option locked" onClick={showStakingNotice}>
                <div className="option-name">Enhanced</div>
                <div className="option-value">{getProductMaxAPY(type)}</div>
                <div className="coming-soon-badge">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 14.5V16.5M7 10.5V8.5C7 5.73858 9.23858 3.5 12 3.5C14.7614 3.5 17 5.73858 17 8.5V10.5M8.8 21.5H15.2C16.8802 21.5 17.7202 21.5 18.362 21.173C18.9265 20.8854 19.3854 20.4265 19.673 19.862C20 19.2202 20 18.3802 20 16.7V15.3C20 13.6198 20 12.7798 19.673 12.138C19.3854 11.5735 18.9265 11.1146 18.362 10.827C17.7202 10.5 16.8802 10.5 15.2 10.5H8.8C7.11984 10.5 6.27976 10.5 5.63803 10.827C5.07354 11.1146 4.6146 11.5735 4.32698 12.138C4 12.7798 4 13.6198 4 15.3V16.7C4 18.3802 4 19.2202 4.32698 19.862C4.6146 20.4265 5.07354 20.8854 5.63803 21.173C6.27976 21.5 7.11984 21.5 8.8 21.5Z" 
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Coming Soon
                </div>
              </div>
              <div className="staking-option locked" onClick={showStakingNotice}>
                <div className="option-name">Maximum</div>
                <div className="option-value">{getProductMaximumAPY(type)}</div>
                <div className="coming-soon-badge">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 14.5V16.5M7 10.5V8.5C7 5.73858 9.23858 3.5 12 3.5C14.7614 3.5 17 5.73858 17 8.5V10.5M8.8 21.5H15.2C16.8802 21.5 17.7202 21.5 18.362 21.173C18.9265 20.8854 19.3854 20.4265 19.673 19.862C20 19.2202 20 18.3802 20 16.7V15.3C20 13.6198 20 12.7798 19.673 12.138C19.3854 11.5735 18.9265 11.1146 18.362 10.827C17.7202 10.5 16.8802 10.5 15.2 10.5H8.8C7.11984 10.5 6.27976 10.5 5.63803 10.827C5.07354 11.1146 4.6146 11.5735 4.32698 12.138C4 12.7798 4 13.6198 4 15.3V16.7C4 18.3802 4 19.2202 4.32698 19.862C4.6146 20.4265 5.07354 20.8854 5.63803 21.173C6.27976 21.5 7.11984 21.5 8.8 21.5Z" 
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Coming Soon
                </div>
              </div>
            </div>
            <div className="staking-info">
              <p>{getStakingInfoText(type)}</p>
              <p className="uptime-note">Requires 85% minimum uptime for full rewards</p>
            </div>
          </div>
        </div>
        <div className="product-footer">
          <button className="btn" style={{ flex: 1 }} onClick={() => openPurchaseModal(type)}>Buy Now</button>
          <button onClick={() => openInfoModal(type)} className="btn btn-outline">Learn More</button>
        </div>
      </div>
    );
  };

  // Helper to render product-specific specs
  const renderProductSpecs = (type) => {
    const specs = {
      basic: [
        'Entry-level WiFi connectivity',
        'Token-denominated returns',
        'Perfect for beginners'
      ],
      pro: [
        'Enhanced signal strength',
        'Higher efficiency rate',
        'Best value for money'
      ],
      elite: [
        'Advanced AI optimization',
        'Premium hardware specs',
        'Extended connectivity range'
      ],
      enterprise: [
        '8-device hardware cluster',
        'Commercial-grade performance',
        'Priority network allocation'
      ]
    };

    return specs[type].map((spec, index) => (
      <div className="spec-item" key={index}>
        <svg className="spec-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2"/>
        </svg>
        <span>{spec}</span>
      </div>
    ));
  };

  return (
    <section className="products" id="products">
      <div className="container">
        <h2 className="section-title">Choose Your <span>RWA Device</span></h2>
        <p style={{ textAlign: "center", maxWidth: "800px", margin: "0 auto 2rem", color: "var(--text-secondary)" }}>
          Select the perfect AeroFi device to match your investment goals. Our diverse range offers options for every budget, with enhanced returns through strategic staking.
        </p>
        
        {/* Staking announcement banner */}
        <div className="staking-announcement-banner">
          <div className="announcement-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" 
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="announcement-message">
            <p>Staking feature will be enabled in a future update. All devices currently operate at expected annual yield rates.</p>
          </div>
        </div>
        
        {/* Staking notification popup */}
        {stakingNotification && (
          <div className="staking-notification">
            <div className="notification-content">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" 
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>Staking options will be enabled in an upcoming release.</span>
            </div>
          </div>
        )}
        
        <div className="products-grid">
          {/* AeroNode Basic */}
          {renderProductCard('basic', 'ENTRY LEVEL')}
          
          {/* AeroNode Pro */}
          {renderProductCard('pro', 'POPULAR')}
          
          {/* AeroNode Elite */}
          {renderProductCard('elite', 'PREMIUM')}
          
          {/* AeroNode Enterprise */}
          {renderProductCard('enterprise', 'FLAGSHIP')}
        </div>
      </div>
      
      {/* Info Modal component */}
      <DeviceModal 
        isOpen={infoModalOpen} 
        onClose={closeInfoModal} 
        deviceType={currentDevice}
      />
      
      {/* Purchase Modal component */}
      <PurchaseModal
        isOpen={purchaseModalOpen}
        onClose={closePurchaseModal}
        product={selectedProduct}
        wallet={wallet}
        onPurchase={handlePurchaseComplete}
      />
    </section>
  );
};

export default ProductsGrid;
